import React, { createContext, useCallback, useMemo, useState } from 'react';
import { useLocalStorage } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import {
  ChatConversationSummaryDto,
  ChatUsageDto,
} from 'src/api/FiretrolApiClient';
import { useChatUsage } from 'src/api/queries/chat/useChatUsage';
import { queryKeys } from 'src/api/queries/queryKeys';
import {
  ChatSegment,
  IChatToolActivity,
  IChatUiMessage,
  buildMessageSegments,
  messageText,
} from 'src/api/queries/chat/useChatConversationQueries';
import {
  IComposerDraft,
  INewConversationOptions,
  MAX_CONVERSATION_TITLE_LENGTH,
  useChatConversationLifecycle,
} from 'src/hooks/useChatConversationLifecycle';
import {
  ChatError,
  ISendMessageOptions,
  useChatStream,
} from 'src/hooks/useChatStream';
import { PropsWithChildren } from 'src/PropsWithChildren';
import { getErrorMessage, showErrorToast } from 'src/utils';

export type {
  ChatError,
  ChatSegment,
  IChatToolActivity,
  IChatUiMessage,
  IComposerDraft,
  INewConversationOptions,
  ISendMessageOptions,
};
export { buildMessageSegments, MAX_CONVERSATION_TITLE_LENGTH };

// Drawer open/close state, kept separate from the conversation context so that
// components which only need to trigger the drawer (e.g. the header) don't
// re-render on every streamed token.
interface IChatUiContext {
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
}

const defaultChatUiContext: IChatUiContext = {
  drawerOpen: false,
  openDrawer: () => {},
  closeDrawer: () => {},
};

export const ChatUiContext =
  createContext<IChatUiContext>(defaultChatUiContext);

interface IChatContext {
  conversationId: string | null;
  jobId: number | null;
  conversations: ChatConversationSummaryDto[];
  loadingConversations: boolean;
  loadingConversation: boolean;
  messages: IChatUiMessage[];
  streaming: boolean;
  /** True while a reply streams or a fork is pending; sending, editing, regenerating and forking are refused. */
  busy: boolean;
  /** The user message whose fork is pending in this view; switching conversation clears it. */
  forkingMessageId: string | null;
  error: ChatError | null;
  usage: ChatUsageDto | null;
  composerDraft: IComposerDraft | null;
  consumeComposerDraft: (nonce: number) => void;
  /** Applies to every later send, including regenerate and retry; persisted per browser. */
  thinkHarder: boolean;
  setThinkHarder: (value: boolean) => void;
  /** The active conversation, or the one the next send creates, neither reads nor saves memory notes. */
  memoryOff: boolean;
  newConversation: (options?: INewConversationOptions) => void;
  sendMessage: (text: string, options?: ISendMessageOptions) => Promise<void>;
  /** Resends the question on screen in place of itself (the Regenerate action on the last reply). */
  regenerate: () => void;
  /** Resends the text of the last attempt, e.g. a failed edit's text (the Retry action on the error banner). */
  retryLast: () => void;
  forkAt: (messageId: string) => Promise<void>;
  stop: () => void;
  selectConversation: (id: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  deleteConversationSafely: (id: string) => Promise<void>;
  renameConversationTitle: (id: string, title: string) => Promise<void>;
  refreshConversations: () => Promise<void>;
  refreshUsage: () => Promise<void>;
}

const defaultChatContext: IChatContext = {
  conversationId: null,
  jobId: null,
  conversations: [],
  loadingConversations: false,
  loadingConversation: false,
  messages: [],
  streaming: false,
  busy: false,
  forkingMessageId: null,
  error: null,
  usage: null,
  composerDraft: null,
  consumeComposerDraft: () => {},
  thinkHarder: false,
  setThinkHarder: () => {},
  memoryOff: false,
  newConversation: () => {},
  sendMessage: async () => {},
  regenerate: () => {},
  retryLast: () => {},
  forkAt: async () => {},
  stop: () => {},
  selectConversation: async () => {},
  deleteConversation: async () => {},
  deleteConversationSafely: async () => {},
  renameConversationTitle: async () => {},
  refreshConversations: async () => {},
  refreshUsage: async () => {},
};

export const ChatContext = createContext<IChatContext>(defaultChatContext);

export const ChatProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [thinkHarder, setThinkHarder] = useLocalStorage<boolean>({
    key: 'chat.thinkHarder',
    defaultValue: false,
    getInitialValueInEffect: false,
  });

  const usageQuery = useChatUsage(drawerOpen);
  const usage = usageQuery.data ?? null;
  const refreshUsage = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.chat.usage() });
  }, [queryClient]);

  const {
    conversationId,
    page,
    jobId,
    conversations,
    loadingConversations,
    memoryOff,
    forkingMessageId,
    composerDraft,
    consumeComposerDraft,
    ensureConversation,
    isForkPending,
    startNewConversation,
    openConversation,
    deleteConversationOnServer,
    renameConversationTitle,
    forkConversation,
    refreshConversations,
  } = useChatConversationLifecycle(drawerOpen);

  const {
    messages,
    loadingConversation,
    streaming,
    error,
    sendMessage,
    regenerate,
    retryLast,
    stop,
    resetStream,
    isSending,
    getMessages,
  } = useChatStream({
    conversationId,
    ensureConversation,
    isForkPending,
    page,
    thinkHarder,
    refreshUsage,
  });

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const newConversation = useCallback(
    (options?: INewConversationOptions) => {
      resetStream();
      startNewConversation(options);
    },
    [resetStream, startNewConversation],
  );

  const selectConversation = useCallback(
    async (id: string) => {
      resetStream();
      openConversation(id);
    },
    [resetStream, openConversation],
  );

  const deleteConversation = useCallback(
    async (id: string) => {
      await deleteConversationOnServer(id);
      if (id === conversationId) {
        newConversation();
      }
    },
    [conversationId, newConversation, deleteConversationOnServer],
  );

  const deleteConversationSafely = useCallback(
    async (id: string) => {
      try {
        await deleteConversation(id);
      } catch (err) {
        showErrorToast(getErrorMessage(err));
      }
    },
    [deleteConversation],
  );

  const forkAt = useCallback(
    async (messageId: string) => {
      if (isSending()) return;
      const source = getMessages().find((m) => m.id === messageId);
      await forkConversation(
        messageId,
        source ? messageText(source) : '',
        resetStream,
      );
    },
    [isSending, getMessages, forkConversation, resetStream],
  );

  const busy = streaming || forkingMessageId !== null;

  const uiContextValue = useMemo<IChatUiContext>(
    () => ({ drawerOpen, openDrawer, closeDrawer }),
    [drawerOpen, openDrawer, closeDrawer],
  );

  const chatContextValue = useMemo<IChatContext>(
    () => ({
      conversationId,
      jobId,
      conversations,
      loadingConversations,
      loadingConversation,
      messages,
      streaming,
      busy,
      forkingMessageId,
      error,
      usage,
      composerDraft,
      consumeComposerDraft,
      thinkHarder,
      setThinkHarder,
      memoryOff,
      newConversation,
      sendMessage,
      regenerate,
      retryLast,
      forkAt,
      stop,
      selectConversation,
      deleteConversation,
      deleteConversationSafely,
      renameConversationTitle,
      refreshConversations,
      refreshUsage,
    }),
    [
      conversationId,
      jobId,
      conversations,
      loadingConversations,
      loadingConversation,
      messages,
      streaming,
      busy,
      forkingMessageId,
      error,
      usage,
      thinkHarder,
      setThinkHarder,
      composerDraft,
      consumeComposerDraft,
      newConversation,
      sendMessage,
      regenerate,
      retryLast,
      forkAt,
      stop,
      selectConversation,
      deleteConversation,
      deleteConversationSafely,
      renameConversationTitle,
      refreshConversations,
      refreshUsage,
      memoryOff,
    ],
  );

  return (
    <ChatUiContext.Provider value={uiContextValue}>
      <ChatContext.Provider value={chatContextValue}>
        {children}
      </ChatContext.Provider>
    </ChatUiContext.Provider>
  );
};

/** Drawer open/close only — safe for components that must not re-render on every streamed token. */
export const useChatUi: () => IChatUiContext = () => {
  return React.useContext(ChatUiContext);
};

export const useChat: () => IChatContext = () => {
  const context = React.useContext(ChatContext);
  return context;
};
